// The single player-safe combat projection used by both the player presentation
// and the DM's exact preview. It returns no source object and no DM-only fields.

import { activeFamilyOf, familyArrayKey } from '../encounter/encounterStoreEngine';

export const DEFAULT_PLAYER_VIEW_SETTINGS = Object.freeze({
  showEnemyNames: true,
  showEnemyHP: false,
  showConditions: true,
  showPlayerHP: true,
  showObjective: false
});

function populated(monster) {
  if (!monster) return false;
  return Boolean((monster.customName && String(monster.customName).trim()) ||
    (monster.selectedMonster && String(monster.selectedMonster).trim()));
}

function safeName(monster, index) {
  const custom = monster && monster.customName ? String(monster.customName).trim() : '';
  const selected = monster && monster.selectedMonster ? String(monster.selectedMonster).trim() : '';
  return custom || selected || ('Enemy ' + (index + 1));
}

function statusWord(current, maximum) {
  let cur = Number(current);
  const max = Number(maximum);
  if (Number.isNaN(max) || max <= 0) return '';
  if (Number.isNaN(cur)) cur = max;
  if (cur <= 0) return 'Down';
  return ((cur / max) * 100) <= 50 ? 'Bloodied' : 'Healthy';
}

function safeConditions(value) {
  if (!value || !Array.isArray(value.conditions)) return [];
  return value.conditions.map(function (condition) {
    if (typeof condition === 'string') return condition;
    if (condition && typeof condition === 'object') return condition.name || condition.type || condition.label || '';
    return '';
  }).filter(function (name) { return Boolean(name && String(name).trim()); });
}

export function buildPlayerSafeCombatProjection(state, requestedSettings) {
  const settings = { ...DEFAULT_PLAYER_VIEW_SETTINGS, ...(requestedSettings || {}) };
  const store = state && state.encounterStore ? state.encounterStore : {};
  const activeIndex = typeof store.activeIndex === 'number' ? store.activeIndex : 0;
  const family = activeFamilyOf(store);
  const familyKey = familyArrayKey(family);
  const encounters = Array.isArray(store[familyKey]) ? store[familyKey] : [];
  const encounter = encounters[activeIndex];
  const encounterName = encounter && encounter.meta && encounter.meta.name ? encounter.meta.name : ('Encounter ' + (activeIndex + 1));
  const objective = encounter && encounter.encounterBrief && encounter.encounterBrief.goal && encounter.encounterBrief.goal.objective
    ? encounter.encounterBrief.goal.objective : '';
  const players = Array.isArray(state && state.players) ? state.players : [];
  const monsters = Array.isArray(state && state.monsters) ? state.monsters : [];
  const currentCombatantIndex = state && typeof state.currentCombatantIndex === 'number' ? state.currentCombatantIndex : -1;

  return {
    encounter: encounterName,
    round: state && typeof state.currentRound === 'number' ? state.currentRound : 1,
    objective: settings.showObjective && objective.trim() ? objective : null,
    party: players.map(function (player, index) {
      const name = player && player.name && String(player.name).trim() ? player.name : ('Player ' + (index + 1));
      return {
        key: 'p:' + String(player && player.id != null ? player.id : index),
        name: name,
        status: settings.showPlayerHP && player && typeof player.hp !== 'undefined' && typeof player.maxHp !== 'undefined'
          ? (player.hp + ' / ' + player.maxHp) : statusWord(player && player.hp, player && player.maxHp),
        conditions: settings.showConditions ? safeConditions(player) : [],
      };
    }),
    enemies: monsters.map(function (monster, index) { return { monster, index }; })
      .filter(function (entry) { return populated(entry.monster); })
      .map(function (entry) {
        const monster = entry.monster;
        return {
          key: 'm:' + entry.index,
          name: settings.showEnemyNames ? safeName(monster, entry.index) : ('Enemy ' + (entry.index + 1)),
          status: settings.showEnemyHP && typeof monster.currentHP !== 'undefined' && typeof monster.maxHP !== 'undefined'
            ? (monster.currentHP + ' / ' + monster.maxHP) : statusWord(monster.currentHP, monster.maxHP),
          conditions: settings.showConditions ? safeConditions(monster) : [],
          current: entry.index === currentCombatantIndex,
        };
      }),
  };
}
